import { Injectable } from '@angular/core';

import { AnalyticsService } from '@guardfinder/analytics';

import { Observable, tap } from 'rxjs';

import { ApiClient } from '../../../../api-client';
import { UnitResult } from '../../../../error';
import { JobSubmittedEvent } from '../../../../guard/Infrastructure/Event/job-submitted.event';
import { Job } from '../../../Domain/Model/CreateJob/create-job.model';
import { JobId } from '../../../Domain/Model/Job/VO/job-id';
import { BackendCreateJobService } from './backend-create-job.service';

/**
 * Decorator for the BackendCreateJobService that adds analytics tracking functionality.
 * This service extends the base job creation functionality by logging analytics events
 * when jobs are successfully created.
 */
@Injectable({
    providedIn: 'root'
})
export class AnalyticsBackendCreateJobServiceDecorator extends BackendCreateJobService {
    /**
     * Creates an instance of AnalyticsBackendCreateJobServiceDecorator.
     *
     * @param {ApiClient} apiClient - The API client used to make HTTP requests to the backend
     * @param {AnalyticsService} analytics - The analytics service used to log events
     */
    constructor(
        apiClient: ApiClient,
        private readonly analytics: AnalyticsService
    ) {
        super(apiClient);
    }

    /**
     * Creates a job and logs an analytics event when the job is successfully created.
     *
     * @param {Job} model - The job model containing all the information needed to create a job
     * @returns {Observable<UnitResult<JobId>>} An observable that emits a UnitResult containing
     *                                          the ID of the created job if successful
     */
    override createJob(model: Job): Observable<UnitResult<JobId>> {
        return super.createJob(model).pipe(
            tap(result => {
                if (!result.isSuccess()) return;

                const event = new JobSubmittedEvent(result.getResult());
                this.analytics.logCustomEvent(event).then();
            })
        );
    }
}
